import React from 'react';
import { FiX as X, FiCopy as Copy, FiCheck as Check, FiClock as Clock } from 'react-icons/fi';

interface ShareLinkData {
  share_url: string;
  days_until_expiry: number;
  expires_at: string;
}

interface ShareLinkModalProps {
  show: boolean;
  shareLinkData: ShareLinkData | null;
  shareLinkError: string | null;
  linkCopied: boolean;
  onClose: () => void;
  onCopy: () => void;
}

const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  show,
  shareLinkData,
  shareLinkError,
  linkCopied,
  onClose,
  onCopy,
}) => {
  if (!show || !shareLinkData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 transition-colors">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white transition-colors">Share Assessment Link</h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors">
            Anyone with this link can access and collaborate on this assessment. The link will expire in {shareLinkData.days_until_expiry} days.
          </p>

          {shareLinkError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-600 dark:text-red-400">{shareLinkError}</p>
            </div>
          )}

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 overflow-hidden">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Assessment Link</p>
                <p className="text-sm text-gray-900 dark:text-white truncate font-mono">
                  {shareLinkData.share_url}
                </p>
              </div>
              <button
                onClick={onCopy}
                className={`flex-shrink-0 flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  linkCopied
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
                title={linkCopied ? "Copied!" : "Copy to clipboard"}
              >
                {linkCopied ? (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="flex items-start space-x-2 text-sm text-gray-600 dark:text-gray-400">
            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p>Expires on: {new Date(shareLinkData.expires_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</p>
              <p className="text-xs mt-1">Valid for 30 days from creation</p>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareLinkModal;
